import { SUBSCRIPTION_LIMITS } from '../config/limits';

import { SiteData, stripeApi } from './api';

export interface StripeSubscription {
  current_period_end?: number;
  current_period_start?: number;
  status?: string;
  interval?: string;
}

export interface DaysRemainingInfo {
  daysRemaining: number;
  totalDays: number;
  isExpired: boolean;
  expirationDate: Date;
  description: string;
}

/**
 * Calculate days remaining for a free plan site
 * Free plans expire after 14 days from creation
 */
export function calculateFreePlanDaysRemaining(site: SiteData): DaysRemainingInfo {
  const createdDate = new Date(site.createdAt);
  const now = new Date();
  const freePlanDuration = SUBSCRIPTION_LIMITS.FREE.FREE_PLAN_DURATION_DAYS;
  
  // Calculate expiration date (createdAt + 14 days)
  const expirationDate = new Date(createdDate);
  expirationDate.setDate(expirationDate.getDate() + freePlanDuration);
  
  // Calculate days remaining
  const timeDiff = expirationDate.getTime() - now.getTime();
  const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));
  
  const isExpired = daysRemaining <= 0;
  
  return {
    daysRemaining: Math.max(0, daysRemaining),
    totalDays: freePlanDuration,
    isExpired,
    expirationDate,
    description: isExpired 
      ? 'Free plan has expired'
      : `Free plan expires in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`
  };
}

/**
 * Calculate days remaining for a paid plan site
 * Fetches actual billing cycle information from Stripe subscription details.
 */
export async function calculatePaidPlanDaysRemaining(site: SiteData): Promise<DaysRemainingInfo> {
  const now = new Date();
  
  // Default billing cycle (fallback)
  let billingCycleDays = 30; // Monthly
  let nextBillingDate: Date;
  
  try {
    // Fetch actual subscription details from Stripe if subscription ID is available
    if (site.stripeSubscriptionId) {
      const subscriptionData = await stripeApi.getSubscription(site.stripeSubscriptionId) as StripeSubscription;
      
      if (subscriptionData && subscriptionData.current_period_end) {
        // Use actual next billing date from Stripe
        nextBillingDate = new Date(subscriptionData.current_period_end * 1000); // Convert from Unix timestamp
        
        // Calculate actual billing cycle days for display purposes
        if (subscriptionData.current_period_start) {
          const currentPeriodStart = new Date(subscriptionData.current_period_start * 1000);
          billingCycleDays = Math.ceil((nextBillingDate.getTime() - currentPeriodStart.getTime()) / (1000 * 3600 * 24));
        }
      } else {
        throw new Error('Invalid subscription data');
      }
    } else {
      throw new Error('No subscription ID available');
    }
  } catch (error) {
    // Fallback to manual calculation if Stripe API fails
    console.warn('Could not fetch Stripe subscription details, falling back to manual calculation:', error);
    
    // Use lastPaymentAt if available, otherwise fallback to subscriptionCreatedAt
    const lastBillingDate = site.lastPaymentAt 
      ? new Date(site.lastPaymentAt)
      : site.subscriptionCreatedAt 
        ? new Date(site.subscriptionCreatedAt)
        : new Date(site.createdAt);
    
    // Calculate next billing date using default 30-day cycle
    nextBillingDate = new Date(lastBillingDate);
    nextBillingDate.setDate(nextBillingDate.getDate() + billingCycleDays);
  }
  
  // Calculate days remaining
  const timeDiff = nextBillingDate.getTime() - now.getTime();
  const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));
  
  const isExpired = daysRemaining <= 0;
  
  return {
    daysRemaining: Math.max(0, daysRemaining),
    totalDays: billingCycleDays,
    isExpired,
    expirationDate: nextBillingDate,
    description: isExpired
      ? 'Billing cycle has ended'
      : `Next billing in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`
  };
}

/**
 * Calculate days remaining for any site based on its plan type
 */
export async function calculateDaysRemaining(site: SiteData): Promise<DaysRemainingInfo> {
  if (site.plan === 'free') {
    return calculateFreePlanDaysRemaining(site);
  } else {
    return await calculatePaidPlanDaysRemaining(site);
  }
}

/**
 * Calculate overall days remaining for the user's primary plan
 * Business model: Paid users have ONE subscription covering ALL sites
 */
export async function calculateOverallDaysRemaining(sites: SiteData[]): Promise<DaysRemainingInfo | null> {
  if (!sites || sites.length === 0) {
    return null;
  }

  // Check if user has any paid sites (indicates paid subscription)
  const paidSites = sites.filter(site => site.plan !== 'free');
  
  if (paidSites.length > 0) {
    // User has a paid subscription - use any paid site to get billing info
    // All paid sites should share the same subscription
    const primaryPaidSite = paidSites.find(site => site.stripeSubscriptionId) || paidSites[0];
    return await calculatePaidPlanDaysRemaining(primaryPaidSite);
  }

  // User only has free sites - find the earliest expiring one
  if (sites.every(site => site.plan === 'free')) {
    const freeSitesWithDays = sites.map(site => ({
      site,
      daysInfo: calculateFreePlanDaysRemaining(site)
    }));
    
    const earliestExpiring = freeSitesWithDays.reduce((earliest, current) => 
      current.daysInfo.daysRemaining < earliest.daysInfo.daysRemaining ? current : earliest
    );
    
    return earliestExpiring.daysInfo;
  }

  return null;
}

/**
 * Get the status color for days remaining display
 */
export function getDaysRemainingColor(daysRemaining: number, isFreePlan: boolean): string {
  if (daysRemaining <= 0) {
    return 'text-red-600';
  }
  
  const warningThreshold = isFreePlan ? 3 : 7; // 3 days for free, 7 days for paid
  
  if (daysRemaining <= warningThreshold) {
    return 'text-yellow-600';
  }
  
  return 'text-green-600';
}

/**
 * Format days remaining for display
 */
export function formatDaysRemaining(daysInfo: DaysRemainingInfo): string {
  if (daysInfo.isExpired) {
    return 'Expired';
  }
  
  if (daysInfo.daysRemaining === 0) {
    return 'Expires today';
  }
  
  return `${daysInfo.daysRemaining} day${daysInfo.daysRemaining !== 1 ? 's' : ''} remaining`;
}